import { useEffect } from 'react';
import { useEditor, EditorContent, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import TiptapUnderline from '@tiptap/extension-underline';
import TextAlign from '@tiptap/extension-text-align';
import TiptapLink from '@tiptap/extension-link';
import Placeholder from '@tiptap/extension-placeholder';
import DOMPurify from 'dompurify';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faAlignCenter,
  faAlignLeft,
  faAlignRight,
  faBold,
  faItalic,
  faLink,
  faListOl,
  faListUl,
  faQuoteRight,
  faRotateLeft,
  faRotateRight,
  faStrikethrough,
  faUnderline,
} from '@fortawesome/free-solid-svg-icons';
import { cn } from '@/lib/cn';

/**
 * Shared TipTap rich-text editor for email template bodies.
 *
 * Extracted from CommunicationTemplatesPage's EditModal so the Platform Console
 * library reuses it instead of adding a third TipTap implementation to the
 * codebase (COMMUNICATION_DESIGN.md §5.7 — D3). Extension set, sanitisation and
 * toolbar behaviour are carried over unchanged.
 *
 * Images and table editing are intentionally absent — they need extensions that
 * are not installed and, for images, an upload/storage decision (§9, Phase D).
 */

interface ToolbarButtonProps {
  onClick: () => void;
  active?: boolean;
  disabled?: boolean;
  title: string;
  children: React.ReactNode;
  /**
   * Fire on `click` instead of `mousedown`.
   *
   * The default `mousedown` + `preventDefault()` exists to stop the editor
   * losing its selection before a formatting command runs. That is wrong for
   * actions which must open a native dialog (a file picker): suppressing the
   * default mousedown behaviour can stop the browser treating the gesture as
   * the user activation such dialogs require.
   */
  activateOnClick?: boolean;
}

export function ToolbarButton({
  onClick,
  active,
  disabled,
  title,
  children,
  activateOnClick,
}: ToolbarButtonProps) {
  return (
    <button
      type="button"
      {...(activateOnClick
        ? { onClick: () => !disabled && onClick() }
        : {
            onMouseDown: (e: React.MouseEvent) => {
              // Prevent the editor losing selection before the command runs.
              e.preventDefault();
              if (!disabled) onClick();
            },
          })}
      title={title}
      aria-label={title}
      aria-pressed={active ?? false}
      disabled={disabled}
      className={cn(
        'flex h-7 min-w-[28px] items-center justify-center rounded px-1.5 text-xs transition-colors',
        active ? 'bg-primary/20 text-primary' : 'text-foreground hover:bg-muted',
        disabled && 'cursor-not-allowed opacity-40',
      )}
    >
      {children}
    </button>
  );
}

interface TemplateEditorProps {
  /** Current HTML value. Applied to the editor only when it differs, to avoid cursor resets. */
  value: string;
  onChange: (html: string) => void;
  disabled?: boolean;
  placeholder?: string;
  /** Receives the editor instance so the parent can insert variables at the cursor. */
  onEditorReady?: (editor: Editor | null) => void;
  className?: string;
  /** Tailwind arbitrary-variant min-height for the editable area. */
  minHeightClass?: string;
}

export function TemplateEditor({
  value,
  onChange,
  disabled,
  placeholder = 'Write your email HTML body here…',
  onEditorReady,
  className,
  minHeightClass = '[&_.ProseMirror]:min-h-[160px]',
}: TemplateEditorProps) {
  const editor = useEditor({
    extensions: [
      StarterKit.configure({ codeBlock: false }),
      TiptapUnderline,
      TextAlign.configure({ types: ['heading', 'paragraph'] }),
      TiptapLink.configure({ openOnClick: false }),
      Placeholder.configure({ placeholder }),
    ],
    content: value || '',
    editable: !disabled,
    onUpdate: ({ editor: ed }) => {
      onChange(DOMPurify.sanitize(ed.getHTML()));
    },
  });

  useEffect(() => {
    onEditorReady?.(editor);
  }, [editor, onEditorReady]);

  // Adopt external value changes (e.g. loading a different template) without
  // clobbering what the user is typing.
  useEffect(() => {
    if (editor && value !== editor.getHTML()) {
      editor.commands.setContent(value || '', false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, editor]);

  useEffect(() => {
    editor?.setEditable(!disabled);
  }, [editor, disabled]);

  // Ported verbatim from the tenant EditModal so the extraction does not
  // downgrade its link UX to a native prompt.
  const setLink = async (): Promise<void> => {
    if (!editor) return;
    const prev = editor.getAttributes('link').href as string | undefined;
    const { isConfirmed, value } = await Swal.fire<string>({
      title: 'Insert Link',
      input: 'url',
      inputValue: prev ?? '',
      inputPlaceholder: 'https://example.com',
      showCancelButton: true,
      confirmButtonText: 'Apply',
      cancelButtonText: prev ? 'Remove Link' : 'Cancel',
      confirmButtonColor: 'hsl(74 40% 40%)',
      cancelButtonColor: prev ? 'hsl(0 84% 60%)' : 'hsl(74 15% 45%)',
      reverseButtons: true,
      customClass: { popup: 'rounded-xl' },
      inputValidator: (v) => {
        if (v && !/^https?:\/\/.+/.test(v))
          return 'Enter a valid URL starting with http:// or https://';
      },
    });
    if (isConfirmed) {
      if (!value) {
        editor.chain().focus().unsetLink().run();
      } else {
        editor.chain().focus().setLink({ href: value }).run();
      }
    } else if (prev) {
      editor.chain().focus().unsetLink().run();
    }
  };

  if (!editor) return null;

  return (
    <div className={cn('overflow-hidden rounded-md border border-input', className)}>
      <div
        role="toolbar"
        aria-label="Formatting"
        className="flex flex-wrap items-center gap-0.5 border-b border-border bg-muted/30 px-1.5 py-1"
      >
        <ToolbarButton
          onClick={() => editor.chain().focus().undo().run()}
          title="Undo"
          disabled={disabled}
        >
          <FontAwesomeIcon icon={faRotateLeft} className="h-3 w-3" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().redo().run()}
          title="Redo"
          disabled={disabled}
        >
          <FontAwesomeIcon icon={faRotateRight} className="h-3 w-3" />
        </ToolbarButton>

        <span className="mx-1 h-4 w-px bg-border" />

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBold().run()}
          active={editor.isActive('bold')}
          title="Bold"
          disabled={disabled}
        >
          <FontAwesomeIcon icon={faBold} className="h-3 w-3" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleItalic().run()}
          active={editor.isActive('italic')}
          title="Italic"
          disabled={disabled}
        >
          <FontAwesomeIcon icon={faItalic} className="h-3 w-3" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleUnderline().run()}
          active={editor.isActive('underline')}
          title="Underline"
          disabled={disabled}
        >
          <FontAwesomeIcon icon={faUnderline} className="h-3 w-3" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleStrike().run()}
          active={editor.isActive('strike')}
          title="Strikethrough"
          disabled={disabled}
        >
          <FontAwesomeIcon icon={faStrikethrough} className="h-3 w-3" />
        </ToolbarButton>

        <span className="mx-1 h-4 w-px bg-border" />

        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBulletList().run()}
          active={editor.isActive('bulletList')}
          title="Bullet list"
          disabled={disabled}
        >
          <FontAwesomeIcon icon={faListUl} className="h-3 w-3" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleOrderedList().run()}
          active={editor.isActive('orderedList')}
          title="Ordered list"
          disabled={disabled}
        >
          <FontAwesomeIcon icon={faListOl} className="h-3 w-3" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().toggleBlockquote().run()}
          active={editor.isActive('blockquote')}
          title="Blockquote"
          disabled={disabled}
        >
          <FontAwesomeIcon icon={faQuoteRight} className="h-3 w-3" />
        </ToolbarButton>

        <span className="mx-1 h-4 w-px bg-border" />

        <ToolbarButton
          onClick={() => editor.chain().focus().setTextAlign('left').run()}
          active={editor.isActive({ textAlign: 'left' })}
          title="Align left"
          disabled={disabled}
        >
          <FontAwesomeIcon icon={faAlignLeft} className="h-3 w-3" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().setTextAlign('center').run()}
          active={editor.isActive({ textAlign: 'center' })}
          title="Align center"
          disabled={disabled}
        >
          <FontAwesomeIcon icon={faAlignCenter} className="h-3 w-3" />
        </ToolbarButton>
        <ToolbarButton
          onClick={() => editor.chain().focus().setTextAlign('right').run()}
          active={editor.isActive({ textAlign: 'right' })}
          title="Align right"
          disabled={disabled}
        >
          <FontAwesomeIcon icon={faAlignRight} className="h-3 w-3" />
        </ToolbarButton>

        <span className="mx-1 h-4 w-px bg-border" />

        <ToolbarButton
          onClick={() => void setLink()}
          active={editor.isActive('link')}
          title="Insert link"
          disabled={disabled}
        >
          <FontAwesomeIcon icon={faLink} className="h-3 w-3" />
        </ToolbarButton>
      </div>

      {/* Prose styling carried over verbatim from the tenant EditModal so the
          extraction is visually neutral for the page it came from. */}
      <EditorContent
        editor={editor}
        className={cn(
          'prose prose-sm max-w-none px-3 py-2.5 text-foreground dark:prose-invert',
          '[&_.ProseMirror]:outline-none',
          '[&_.ProseMirror_p.is-editor-empty:first-child::before]:content-[attr(data-placeholder)]',
          '[&_.ProseMirror_p.is-editor-empty:first-child::before]:text-muted-foreground',
          '[&_.ProseMirror_p.is-editor-empty:first-child::before]:float-left',
          '[&_.ProseMirror_p.is-editor-empty:first-child::before]:pointer-events-none',
          '[&_.ProseMirror_a]:text-primary [&_.ProseMirror_a]:underline',
          minHeightClass,
        )}
      />
    </div>
  );
}

/** Insert a `{{variable}}` token at the current cursor position. */
export function insertVariable(editor: Editor | null, key: string): void {
  editor?.chain().focus().insertContent(`{{${key}}}`).run();
}
